import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { Link } from 'react-router';
import { ExternalLink, RefreshCw, X } from 'lucide-react';
import {
  creatureApi,
  pf2eApi,
  type Creature,
  type PF2eKnowledgeArticleResponse,
  type PF2eKnowledgeStatus,
} from '../api/client';
import { useLocalization, useTranslation } from '../i18n/provider';
import {
  KnowledgeArticleBody,
  KnowledgeArticleTitle,
} from './KnowledgeArticle';
import { knowledgeArticlePath, knowledgeTargetKey } from '../utils/knowledgeRoutes';
import { Button } from './ui/Button';
import { Select } from './ui/Select';
import type { OrcKnowledgeLinkTarget } from './OrcKnowledgeModalContext';
import { CreatureStatblockView } from './CreatureStatblockView';
import {
  isCreatureAdjustmentAvailable,
  projectCreatureAdjustment,
  type CreatureAdjustment,
} from '../utils/creatureAdjustments';
import { createWizardLogger } from '../utils/wizardLogger';
import './OrcKnowledgeModal.css';

interface OrcKnowledgeModalProps {
  target: OrcKnowledgeLinkTarget | null;
  onClose: () => void;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const STATUS_FALLBACKS: Record<Exclude<PF2eKnowledgeStatus, 'ok'>, string> = {
  missing: 'This entry is not available yet.',
  blocked_non_orc: 'This entry is not available under the ORC license.',
  blocked_license_unknown: 'This entry needs a license review before it can be shown.',
  ambiguous: 'This link points to more than one possible entry.',
  deferred_journal: 'Journal articles are not connected yet.',
  synthetic_token: 'This rules token does not have a standalone article.',
};
const knowledgeLogger = createWizardLogger('encounter.detail');

function fullArticlePath(article: PF2eKnowledgeArticleResponse | null, current: OrcKnowledgeLinkTarget): string {
  const target = article?.target;
  const type = target?.type || current.type;
  const slug = target?.slug || current.id;
  return knowledgeArticlePath(type, slug);
}

function statusFallback(status: PF2eKnowledgeStatus): string {
  if (status === 'ok') return '';
  return STATUS_FALLBACKS[status] || STATUS_FALLBACKS.missing;
}

export default function OrcKnowledgeModal({ target, onClose }: OrcKnowledgeModalProps) {
  const t = useTranslation();
  const { language } = useLocalization();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [current, setCurrent] = useState<OrcKnowledgeLinkTarget | null>(target);
  const [article, setArticle] = useState<PF2eKnowledgeArticleResponse | null>(null);
  const [creature, setCreature] = useState<Creature | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);

  useEffect(() => {
    setCurrent(target);
    setArticle(null);
    setCreature(null);
    setError(null);
    setSelectedVariant(null);
    setLoadState(target ? 'loading' : 'idle');
  }, [target]);

  useEffect(() => {
    setSelectedVariant(null);
  }, [current?.id, current?.type]);

  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    setLoadState('loading');
    setError(null);
    const request = current.type === 'creature'
      ? creatureApi.getCreatureById(
        current.id,
        language,
        selectedVariant || undefined,
      )
      : pf2eApi.getOrcTarget(current.type, current.id, language);
    request
      .then((response) => {
        if (cancelled) return;
        if (current.type === 'creature') {
          setCreature(response.data as Creature);
          setArticle(null);
        } else {
          setArticle(response.data as PF2eKnowledgeArticleResponse);
          setCreature(null);
        }
        setLoadState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        knowledgeLogger.warn('Knowledge detail load failed', {
          target_type: current.type,
          locale: language,
          variant_selected: Boolean(selectedVariant),
        });
        setArticle(null);
        setCreature(null);
        setError(err instanceof Error ? err.message : t('knowledge.modal.errorBody', {}, 'Unable to load this entry.'));
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [current, language, reloadToken, selectedVariant, t]);

  const displayTitle = useMemo(() => {
    if (current?.type === 'creature' && creature) {
      return creature.localized_name || creature.name;
    }
    if (article?.status === 'ok' && current) {
      return article.localized_title || article.title || current.label || t('knowledge.modal.titleFallback', {}, 'Knowledge entry');
    }
    return current?.label || t('knowledge.modal.titleFallback', {}, 'Knowledge entry');
  }, [article, creature, current, t]);
  const displayedCreatureAdjustment = useMemo<CreatureAdjustment>(() => {
    const requested = current?.creatureAdjustment || 'normal';
    if (
      !creature
      || !isCreatureAdjustmentAvailable(creature.level, requested, creature.hp)
    ) {
      return 'normal';
    }
    return requested;
  }, [creature, current?.creatureAdjustment]);
  const displayedCreature = useMemo(() => (
    creature
      ? projectCreatureAdjustment(
        creature,
        displayedCreatureAdjustment,
      )
      : null
  ), [creature, displayedCreatureAdjustment]);

  const handleNavigate = useCallback((nextTarget: OrcKnowledgeLinkTarget) => {
    if (!current || !nextTarget.type || !nextTarget.id || knowledgeTargetKey(nextTarget.type, nextTarget.id) === knowledgeTargetKey(current.type, current.id)) return;
    setCurrent(nextTarget);
    setArticle(null);
    setCreature(null);
  }, [current]);

  if (!target || !current) return null;

  const status = article?.status || 'missing';
  const isCreature = current.type === 'creature';
  const isUnavailable = !isCreature && loadState === 'ready' && status !== 'ok';
  const articleHref = fullArticlePath(article, current);

  return (
    <BaseDialog.Root
      open={!!target}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="ui-dialog__backdrop orc-knowledge-modal__backdrop" />
        <BaseDialog.Popup
          className="ui-dialog__popup orc-knowledge-modal"
          initialFocus={closeRef}
        >
        <header className="ui-dialog__header orc-knowledge-modal__header">
          <div className="orc-knowledge-modal__heading">
            <BaseDialog.Title className="ui-dialog__title orc-knowledge-modal__title">
              <KnowledgeArticleTitle
                title={isCreature ? creature?.name : article?.status === 'ok' ? article.title : null}
                localizedTitle={isCreature ? creature?.localized_name : article?.status === 'ok' ? article.localized_title : null}
                fallback={displayTitle}
                englishClassName="orc-knowledge-modal__english-name"
              />
            </BaseDialog.Title>
          </div>
          <BaseDialog.Close
            ref={closeRef}
            className="ui-dialog__header-close ui-icon-button ui-icon-button--danger-hover ui-icon-button--md"
            aria-label={t('common.close', {}, 'Close')}
            title={t('common.close', {}, 'Close')}
          >
            <X size={18} aria-hidden="true" />
          </BaseDialog.Close>
        </header>

        <div className="ui-dialog__body orc-knowledge-modal__body">
          {loadState === 'loading' ? (
            <div className="orc-knowledge-modal__state" role="status" aria-live="polite">
              <span className="ui-spinner" aria-hidden="true" />
              <span>{t('common.loading', {}, 'Loading...')}</span>
            </div>
          ) : null}

          {loadState === 'error' ? (
            <div className="orc-knowledge-modal__state" role="alert">
              <h3>{t('knowledge.modal.errorTitle', {}, 'Could not load entry')}</h3>
              <p>{error || t('knowledge.modal.errorBody', {}, 'Unable to load this entry.')}</p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                leadingIcon={<RefreshCw size={15} aria-hidden="true" />}
                onClick={() => setReloadToken((value) => value + 1)}
              >
                {t('common.retry', {}, 'Retry')}
              </Button>
            </div>
          ) : null}

          {isUnavailable ? (
            <div className="orc-knowledge-modal__state" role="status">
              <h3>{t('knowledge.modal.unavailableTitle', {}, 'Entry unavailable')}</h3>
              <p>{t(`knowledge.modal.status.${status}`, {}, statusFallback(status))}</p>
            </div>
          ) : null}

          {loadState === 'ready' && article?.status === 'ok' ? (
            <KnowledgeArticleBody article={article} onOrcLinkClick={handleNavigate} />
          ) : null}
          {loadState === 'ready' && isCreature && displayedCreature ? (
            <>
              {displayedCreature.statblock_variants.length ? (
                <div className="orc-knowledge-modal__variant">
                  <Select
                    label={t(
                      'encounter.detail.variantLabel',
                      {},
                      'Creature variant',
                    )}
                    placeholder={t(
                      'encounter.detail.variantPlaceholder',
                      {},
                      'Choose a variant',
                    )}
                    value={selectedVariant}
                    onValueChange={setSelectedVariant}
                    options={displayedCreature.statblock_variants.map((variant) => ({
                      value: variant.key,
                      label: variant.label,
                    }))}
                    portalLayer="modal"
                    searchable={displayedCreature.statblock_variants.length > 10}
                    searchPlaceholder={t(
                      'encounter.detail.variantPlaceholder',
                      {},
                      'Choose a variant',
                    )}
                  />
                </div>
              ) : null}
              {displayedCreatureAdjustment !== 'normal' ? (
                <div className={`orc-knowledge-modal__adjustment orc-knowledge-modal__adjustment--${displayedCreatureAdjustment}`}>
                  <strong>
                    {t(
                      `encounter.adjustment.${displayedCreatureAdjustment}`,
                      {},
                      displayedCreatureAdjustment === 'elite' ? 'Elite' : 'Weak',
                    )}
                  </strong>
                  <span>
                    {t(
                      'encounter.adjustment.projectionHint',
                      {},
                      'Effective level, XP, HP, AC, checks, DCs, saves, attacks, and safely structured damage are adjusted. Spell ranks, slots, frequencies, and narrative ability text are unchanged.',
                    )}
                  </span>
                </div>
              ) : null}
              <CreatureStatblockView
                creature={displayedCreature}
                t={t}
                onOrcLinkClick={handleNavigate}
              />
            </>
          ) : null}
        </div>

        <footer className="ui-dialog__footer orc-knowledge-modal__footer">
          {!isCreature ? (
            <Link
              to={articleHref}
              className="ui-button ui-button--secondary ui-button--sm"
              onClick={onClose}
            >
              <ExternalLink size={15} aria-hidden="true" />
              <span className="ui-button__label">{t('knowledge.modal.openFullArticle', {}, 'Open full article')}</span>
            </Link>
          ) : null}
          <BaseDialog.Close className="ui-button ui-button--primary ui-button--sm">
            <span className="ui-button__label">{t('common.close', {}, 'Close')}</span>
          </BaseDialog.Close>
        </footer>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
